// Locale-aware accessors for Click&Boat boat copy. Unlike event/tour content,
// a boat has NO raw source description to fall back to (the feed's
// `Description` is identical marketplace boilerplate for every listing — see
// docs/click-and-boat-integration.md) — so the fallback here is a structured
// TEMPLATE built from columns (boat_type, city, price), not source text. Every
// consumer that shows a boat's title / description / meta summary should read
// through these rather than touching translation fields directly.
//
// The localized copy is produced by the describe-boats Edge Function; see
// docs/click-and-boat-integration.md.

import type { Boat } from '@todo/core/boats';
import { cities } from '@/lib/events-data';
import type { Locale } from '@todo/core/i18n/locales';
import { truncate } from '@todo/core/seo';
import en from '@/lib/i18n/resources/en.json';
import fr from '@/lib/i18n/resources/fr.json';
import ru from '@/lib/i18n/resources/ru.json';

export type BoatFaqEntry = { q: string; a: string };

type BoatsResource = {
  boats: {
    typeLabel: Record<string, string>;
    template: { title: string; description: string; summary: string; priceSuffix: string };
    cityIntro: Record<string, string>;
    faq: BoatFaqEntry[];
  };
};

const resources: Record<Locale, BoatsResource> = { en, fr, ru };

function cityName(cityId: string | null): string {
  return cities.find((c) => c.id === cityId)?.name ?? 'the French Riviera';
}

/** Localized display label for a boat_type value, falling back to the raw value. */
export function localizedBoatType(boat: Boat, locale: Locale): string {
  const map = resources[locale].boats.typeLabel;
  return (boat.boat_type && map[boat.boat_type]) || boat.boat_type || 'boat';
}

function templateVars(boat: Boat, locale: Locale): Record<string, string> {
  return {
    name: boat.name,
    type: localizedBoatType(boat, locale),
    city: cityName(boat.city_id),
    price: boat.from_price != null ? String(boat.from_price) : '?',
  };
}

function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => vars[key] ?? '');
}

/**
 * The localized title split into the generated title and the uniqueness suffix
 * describe-boats adds when another boat has the same title (see
 * supabase/functions/describe-boats/title-suffixes.ts). Split so a <title> can
 * be shortened without losing the part that makes it unique.
 */
export function localizedBoatTitleParts(boat: Boat, locale: Locale): { base: string; suffix?: string } {
  const base =
    boat.translations?.[locale]?.title ?? fill(resources[locale].boats.template.title, templateVars(boat, locale));
  return { base, suffix: boat.translations?._title_suffix?.[locale] };
}

/** Localized title (plus its uniqueness suffix, if any), falling back to a template built from structured fields. */
export function localizedBoatTitle(boat: Boat, locale: Locale): string {
  const { base, suffix } = localizedBoatTitleParts(boat, locale);
  return suffix ? `${base} · ${suffix}` : base;
}

/** Localized body description, falling back to a structured-field template. */
export function localizedBoatDescription(boat: Boat, locale: Locale): string {
  return (
    boat.translations?.[locale]?.description ??
    fill(resources[locale].boats.template.description, templateVars(boat, locale))
  );
}

/** Localized ≤160-char SEO meta summary, falling back to a structured-field template. */
export function localizedBoatSummary(boat: Boat, locale: Locale): string {
  return (
    boat.translations?.[locale]?.summary ??
    fill(resources[locale].boats.template.summary, templateVars(boat, locale))
  );
}

/**
 * The `<meta name="description">` for a boat page: the summary plus the
 * CURRENT day rate, fitted to 160 chars without ever cutting the price.
 *
 * The price is appended here rather than generated into the summary because
 * describe-boats only regenerates copy when name / type / city change — a
 * generated price went stale the moment Click&Boat repriced the boat. Summaries
 * that already quote a price (the template fallback, and copy generated before
 * this change) are left as they are.
 */
export function localizedBoatMetaDescription(boat: Boat, locale: Locale): string {
  const summary = localizedBoatSummary(boat, locale).replace(/\s+/g, ' ').trim();
  if (boat.from_price == null || /€|\bEUR\b/.test(summary)) return truncate(summary);
  const price = fill(resources[locale].boats.template.priceSuffix, {
    price: String(Math.round(Number(boat.from_price))),
  });
  const head = truncate(summary, 160 - price.length - 1);
  return `${/[.!?…]$/.test(head) ? head : `${head}.`} ${price}`;
}

/**
 * A city's hand-written evergreen boat-rental intro, or undefined if the city
 * has none. Mirrors cityPageCopy (city-content.ts): hand-authored content read
 * directly off the resource JSON, not through react-i18next's t() (which would
 * need `returnObjects` handling for the FAQ array below).
 */
export function boatCityIntro(cityId: string, locale: Locale): string | undefined {
  return resources[locale].boats.cityIntro[cityId];
}

/** The site-wide boat-rental FAQ, for the FAQPage JSON-LD + on-page accordion. */
export function boatFaq(locale: Locale): BoatFaqEntry[] {
  return resources[locale].boats.faq;
}
